import React from 'react';
import { formatCurrency } from '../../utils/financialCalculations';
import { useLanguage } from '../../i18n';
import { useCosts } from '../../context/CostsContext';
import './StatementTables.css';

function PLTable({ financials }) {
  const { t } = useLanguage();
  const { costs } = useCosts();

  const years = Object.keys(financials).filter(k => k !== 'warnings').map(Number).sort((a, b) => a - b);
  const colSpan = years.length + 1;

  const renderRow = (label, getValue, className = '') => (
    <tr className={className}>
      <td className="row-label">{label}</td>
      {years.map(year => (
        <td key={year} className="number-cell">
          {formatCurrency(getValue(financials[year]))}
        </td>
      ))}
    </tr>
  );

  const renderSeparator = (label) => (
    <tr className="separator-row">
      <td colSpan={colSpan}><strong>{label}</strong></td>
    </tr>
  );

  const entityType = costs.taxEntity?.type || 'private';
  const taxLabel = entityType === 'private' || entityType === 'gmbh_co_kg'
    ? t('pl.incomeTax')
    : t('pl.corporateTax');

  // Check if any year has non-zero values for conditional rows
  const hasWerbungskosten = years.some(y => financials[y]?.details?.additionalWerbungskosten > 0);
  const hasCO2Cost = years.some(y => financials[y]?.details?.co2LandlordCost > 0);
  const hasVAT = years.some(y => financials[y]?.details?.vatCollected > 0);
  const hasLossOffset = years.some(y => financials[y]?.lossOffset > 0);

  return (
    <div className="statement-table-container">
      <h3>{t('pl.title')}</h3>
      <p className="statement-subtitle">
        {t('pl.subtitle').replace('{years}', years.length)}
      </p>

      <div className="table-scroll">
        <table className="statement-table">
          <thead>
            <tr>
              <th className="row-label-header">{t('pl.lineItem')}</th>
              {years.map(year => (
                <th key={year}>{t('pl.year').replace('{year}', year)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Revenue Section */}
            {renderSeparator(t('pl.revenue'))}
            {renderRow(t('pl.rentalIncome'), d => d.revenue, 'highlight')}

            {/* Cost of Revenue */}
            {renderSeparator(t('pl.costOfRevenue'))}
            {renderRow(t('pl.maintenance'), d => d.details.maintenanceCost)}
            {renderRow(t('pl.grossProfit'), d => d.grossProfit, 'subtotal')}

            {/* Operating Expenses */}
            {renderSeparator(t('pl.operatingExpenses'))}
            {renderRow(t('pl.fixedOperatingCosts'), d => d.details.fixedCosts)}
            {renderRow(t('pl.renovationExpense'), d => d.details.refitExpense)}
            {hasWerbungskosten && renderRow(t('pl.werbungskosten'), d => d.details.additionalWerbungskosten)}
            {hasCO2Cost && renderRow(t('pl.co2LandlordCost'), d => d.details.co2LandlordCost)}
            {renderRow(t('pl.scheduledCapex'), d => d.details.scheduledCapex)}

            {/* EBITDA */}
            {renderRow(t('pl.ebitda'), d => d.ebitda, 'subtotal highlight')}

            {/* Non-Cash Items */}
            {renderSeparator(t('pl.nonCashItems'))}
            {renderRow(t('pl.depreciation'), d => d.details.depreciation)}

            {/* EBIT */}
            {renderRow(t('pl.ebit'), d => d.ebit, 'subtotal')}

            {/* Financial Items */}
            {renderSeparator(t('pl.financing'))}
            {renderRow(t('pl.interestExpense'), d => d.details.interestExpense)}

            {/* EBT */}
            {renderRow(t('pl.ebt'), d => d.ebt, 'subtotal highlight')}

            {/* Tax */}
            {renderSeparator(t('pl.tax'))}
            {hasLossOffset && renderRow(t('pl.lossOffset'), d => d.lossOffset > 0 ? -d.lossOffset : 0)}
            {renderRow(taxLabel, d => d.taxExpense)}

            {/* Net Income */}
            {renderRow(t('pl.netIncome'), d => d.netIncome, 'total')}

            {/* VAT (only for short-term) */}
            {hasVAT && (
              <>
                {renderSeparator(t('pl.vatSection'))}
                {renderRow(t('pl.vatCollected'), d => d.details.vatCollected)}
                {renderRow(t('pl.vatInputDeduction'), d => -(d.details.vatInputDeduction || 0))}
                {renderRow(t('pl.netVAT'), d => d.details.netVAT, 'subtotal')}
              </>
            )}

            {/* Cash Flow Summary */}
            {renderSeparator(t('pl.cashFlowSummary'))}
            {renderRow(t('pl.cashFromOperations'), d => d.cashFromOperations)}
            {renderRow(t('pl.cashFromInvesting'), d => d.cashFromInvesting)}
            {renderRow(t('pl.cashFromFinancing'), d => d.cashFromFinancing)}
            {renderRow(t('pl.reserveContribution'), d => d.details.reserveContribution)}
            {renderRow(t('pl.freeCashFlow'), d => d.freeCashFlow, 'total highlight')}
          </tbody>
        </table>
      </div>

      <div className="statement-notes">
        <h4>{t('pl.notes')}:</h4>
        <ul>
          <li><strong>EBITDA:</strong> {t('pl.ebitdaNote')}</li>
          <li><strong>EBIT:</strong> {t('pl.ebitNote')}</li>
          <li><strong>{t('pl.freeCashFlow')}:</strong> {t('pl.freeCashFlowNote')}</li>
          <li><strong>{t('pl.depreciation')}:</strong> {t('pl.afaNote')}</li>
        </ul>
      </div>
    </div>
  );
}

export default PLTable;
